import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { PosProjectRow, PosConfigLevel } from "./constants";

function resolveScopedShopId(
    selectedRow: PosProjectRow | null,
    configLevel: PosConfigLevel
): string | null {
    if (configLevel === "franchise") return null;
    const idBoutique = selectedRow?.id_boutique;
    if (typeof idBoutique === "string" && idBoutique.length > 0) {
        return idBoutique;
    }
    const shopId = selectedRow?.shopId;
    if (typeof shopId === "string" && shopId.length > 0) {
        return shopId;
    }
    return null;
}

/**
 * Hook returning a stable predicate `isItemDisabledInGeneral(itemId)` that
 * reflects whether the given template item has been deactivated in the
 * general settings for the relevant scope (franchise / shop / application).
 *
 * Gated on `!isConsultation` so the greying applies to editable contexts
 * (newly created project, newly created application, "en attente" template)
 * but never to a published template opened in read-only mode — published
 * templates are explicitly excluded from general-settings sync.
 *
 * Implementation notes:
 *  - A `Set<string>` of disabled IDs is memoized per scope so the lookup
 *    is O(1) and stable across renders.
 *  - The returned predicate is memoized via `useCallback` so consumers can
 *    safely pass it in their own `useCallback`/`React.memo` dependency
 *    arrays without triggering reconciliation cascades.
 */
export function useIsItemDisabledInGeneral(): (itemId: string) => boolean {
    const itemsDisable = useSelector(
        (state: RootState) => state.posConfig.itemsDisable
    );
    const selectedRow = useSelector(
        (state: RootState) => state.posConfig.selectedRow
    );
    const configLevel = useSelector(
        (state: RootState) => state.posConfig.configLevel
    );
    const isConsultation = useSelector(
        (state: RootState) => state.posConfig.isConsultation
    );

    const scopedShopId = React.useMemo(
        () => resolveScopedShopId(selectedRow, configLevel),
        [selectedRow, configLevel]
    );

    const disabledIds = React.useMemo(() => {
        if (isConsultation) return null;
        const set = new Set<string>();
        for (const d of itemsDisable) {
            if (scopedShopId === null || d.shopId === scopedShopId) {
                set.add(d.id);
            }
        }
        return set;
    }, [itemsDisable, scopedShopId, isConsultation]);

    return React.useCallback(
        (itemId: string) => (disabledIds ? disabledIds.has(itemId) : false),
        [disabledIds]
    );
}
